import { ApiMethodExecutingError } from '../../service/Errors';
import { captureException } from '../../service/ErrorsReporting/ErrorsReporting';
import { Callout, CalloutsEditorId } from '../../typings/banners';
import { CalloutsEditorData } from '../../typings/bannersEditState';
import { ValidationResultItem } from '../../typings/common';

export const OPEN_CALLOUTS_EDITOR = 'OPEN_CALLOUTS_EDITOR';
export const CLOSE_CALLOUTS_EDITOR = 'CLOSE_CALLOUTS_EDITOR';
export const SAVE_CALLOUTS = 'SAVE_CALLOUTS';
export const ADD_CALLOUTS = 'ADD_CALLOUTS';
export const REMOVE_CALLOUTS = 'REMOVE_CALLOUTS';
export const ADD_CALLOUTS_SUCCESS = 'ADD_CALLOUTS_SUCCESS';
export const REMOVE_CALLOUTS_SUCCESS = 'REMOVE_CALLOUTS_SUCCESS';
export const SHOW_ADD_CALLOUTS_ERROR = 'SHOW_ADD_CALLOUTS_ERROR';
export const HIDE_CALLOUTS_ERROR = 'HIDE_CALLOUTS_ERROR';
export const UPDATE_ADD_INPUT_CALLOUTS_EDITOR =
    'UPDATE_ADD_INPUT_CALLOUTS_EDITOR';
export const TOGGLE_CALLOUTS_EDITOR_ITEM = 'TOGGLE_CALLOUTS_EDITOR_ITEM';
export const CALLOUTS_EDITOR_CHECKED_ALL_ITEMS =
    'CALLOUTS_EDITOR_CHECKED_ALL_ITEMS';
export const CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST =
    'CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST';
export const CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST =
    'CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST';

export type OPEN_CALLOUTS_EDITOR = typeof OPEN_CALLOUTS_EDITOR;
export type CLOSE_CALLOUTS_EDITOR = typeof CLOSE_CALLOUTS_EDITOR;
export type SAVE_CALLOUTS = typeof SAVE_CALLOUTS;
export type ADD_CALLOUTS = typeof ADD_CALLOUTS;
export type REMOVE_CALLOUTS = typeof REMOVE_CALLOUTS;
export type ADD_CALLOUTS_SUCCESS = typeof ADD_CALLOUTS_SUCCESS;
export type REMOVE_CALLOUTS_SUCCESS = typeof REMOVE_CALLOUTS_SUCCESS;
export type SHOW_ADD_CALLOUTS_ERROR = typeof SHOW_ADD_CALLOUTS_ERROR;
export type HIDE_CALLOUTS_ERROR = typeof HIDE_CALLOUTS_ERROR;

export type UPDATE_ADD_INPUT_CALLOUTS_EDITOR = typeof UPDATE_ADD_INPUT_CALLOUTS_EDITOR;
export type TOGGLE_CALLOUTS_EDITOR_ITEM = typeof TOGGLE_CALLOUTS_EDITOR_ITEM;
export type CALLOUTS_EDITOR_CHECKED_ALL_ITEMS = typeof CALLOUTS_EDITOR_CHECKED_ALL_ITEMS;
export type CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST = typeof CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST;
export type CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST = typeof CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST;

export interface OpenCalloutsEditor {
    type: OPEN_CALLOUTS_EDITOR;
    bannerId: CalloutsEditorId;
}

export function openCalloutsEditor(
    bannerId: CalloutsEditorId
): OpenCalloutsEditor {
    return {
        type: OPEN_CALLOUTS_EDITOR,
        bannerId
    };
}

export interface CloseCalloutsEditor {
    type: CLOSE_CALLOUTS_EDITOR;
    bannerId: CalloutsEditorId;
}

export function closeCalloutsEditor(
    bannerId: CalloutsEditorId
): CloseCalloutsEditor {
    return {
        type: CLOSE_CALLOUTS_EDITOR,
        bannerId
    };
}

export interface SaveCallouts {
    type: SAVE_CALLOUTS;
    bannerId: CalloutsEditorId[];
}

export function saveCallouts(bannerId: CalloutsEditorId[]): SaveCallouts {
    return {
        type: SAVE_CALLOUTS,
        bannerId
    };
}

export interface UpdateAddInputCalloutsEditor {
    type: UPDATE_ADD_INPUT_CALLOUTS_EDITOR;
    bannerId: CalloutsEditorId;
    text: string;
}

export function updateAddInputCalloutsEditor(
    bannerId: CalloutsEditorId,
    text: string
): UpdateAddInputCalloutsEditor {
    return {
        type: UPDATE_ADD_INPUT_CALLOUTS_EDITOR,
        bannerId,
        text
    };
}

export interface AddCallouts {
    type: ADD_CALLOUTS;
    bannerId: CalloutsEditorId;
    newCallouts: string[];
}

export function addCallouts(
    bannerId: CalloutsEditorId,
    newCallouts: string[]
): AddCallouts {
    return {
        type: ADD_CALLOUTS,
        bannerId,
        newCallouts
    };
}

export interface RemoveCallouts {
    type: REMOVE_CALLOUTS;
    bannerId: CalloutsEditorId;
    calloutIds: string[];
}

export function removeCallouts(
    bannerId: CalloutsEditorId,
    calloutIds: string[]
): RemoveCallouts {
    return {
        type: REMOVE_CALLOUTS,
        bannerId,
        calloutIds
    };
}

export interface DeleteCalloutsSuccess {
    type: REMOVE_CALLOUTS_SUCCESS;
    bannerId: CalloutsEditorId;
    calloutIds: string[];
}

export function deleteCalloutsSuccess(
    bannerId: CalloutsEditorId,
    calloutIds: string[]
): DeleteCalloutsSuccess {
    return {
        type: REMOVE_CALLOUTS_SUCCESS,
        bannerId,
        calloutIds
    };
}

export interface AddCalloutsSuccess {
    type: ADD_CALLOUTS_SUCCESS;
    bannerId: CalloutsEditorId;
    newCallouts: Callout[];
}

export function addCalloutsSuccess(
    bannerId: CalloutsEditorId,
    newCallouts: Callout[]
): AddCalloutsSuccess {
    return {
        type: ADD_CALLOUTS_SUCCESS,
        bannerId,
        newCallouts
    };
}

export interface ShowAddCalloutsError {
    type: SHOW_ADD_CALLOUTS_ERROR;
    bannerId: CalloutsEditorId;
    errors: ValidationResultItem[];
}

export function showAddCalloutsError(
    bannerId: CalloutsEditorId,
    error: ApiMethodExecutingError
): ShowAddCalloutsError {
    let errors = [];
    if (error.getErrors().length === 0) {
        captureException(error.getNativeError());
        errors = [
            {
                code: 'API_ERROR',
                params: null,
                path: ''
            }
        ];
    } else {
        errors = error.getErrors();
    }

    return {
        type: SHOW_ADD_CALLOUTS_ERROR,
        bannerId,
        errors
    };
}

export interface HideCalloutsError {
    type: HIDE_CALLOUTS_ERROR;
    bannerId: CalloutsEditorId;
    paths: string[];
}

export function hideCalloutsError(
    bannerId: CalloutsEditorId,
    paths: string[]
): HideCalloutsError {
    return {
        type: HIDE_CALLOUTS_ERROR,
        bannerId,
        paths
    };
}

export interface ToggleCalloutsEditorItem {
    type: TOGGLE_CALLOUTS_EDITOR_ITEM;
    bannerId: CalloutsEditorId;
    itemId: string;
}

export function toggleCalloutsEditorItem(
    bannerId: CalloutsEditorId,
    itemId: string
): ToggleCalloutsEditorItem {
    return {
        type: TOGGLE_CALLOUTS_EDITOR_ITEM,
        bannerId,
        itemId
    };
}

export interface CalloutsEditorCheckedAllItems {
    type: CALLOUTS_EDITOR_CHECKED_ALL_ITEMS;
    bannerId: CalloutsEditorId;
    isChecked: boolean;
    selectedIds: string[];
}

export function calloutsEditorCheckedAllItems(
    bannerId: CalloutsEditorId,
    isChecked: boolean,
    selectedIds: string[]
): CalloutsEditorCheckedAllItems {
    return {
        type: CALLOUTS_EDITOR_CHECKED_ALL_ITEMS,
        bannerId,
        isChecked,
        selectedIds
    };
}

export interface CalloutsEditorRemoveItemFromCheckList {
    type: CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST;
    bannerId: CalloutsEditorId;
    itemId: string;
}

export function calloutsEditorRemoveItemFromCheckList(
    bannerId: CalloutsEditorId,
    itemId: string
): CalloutsEditorRemoveItemFromCheckList {
    return {
        type: CALLOUTS_EDITOR_REMOVE_ITEM_FROM_CHECKLIST,
        bannerId,
        itemId
    };
}

export interface CalloutsEditorRemoveAllItemsFromCheckList {
    type: CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST;
    bannerId: CalloutsEditorId;
}

export function calloutsEditorRemoveAllItemsFromCheckList(
    bannerId: CalloutsEditorId
): CalloutsEditorRemoveAllItemsFromCheckList {
    return {
        type: CALLOUTS_EDITOR_REMOVE_ALL_ITEMS_FROM_CHECKLIST,
        bannerId
    };
}

export const UPDATE_CALLOUTS_LIST = 'UPDATE_CALLOUTS_LIST';
export type UPDATE_CALLOUTS_LIST = typeof UPDATE_CALLOUTS_LIST;

export interface UpdateCalloutsListAction {
    type: UPDATE_CALLOUTS_LIST;
    list: Callout[];
}

export function updateCalloutsList(list: Callout[]): UpdateCalloutsListAction {
    return {
        type: UPDATE_CALLOUTS_LIST,
        list
    };
}

export const UPDATE_CALLOUTS_DATA = 'UPDATE_CALLOUTS_DATA';
export type UPDATE_CALLOUTS_DATA = typeof UPDATE_CALLOUTS_DATA;

export interface UpdateCalloutsDataAction {
    type: UPDATE_CALLOUTS_DATA;
    data: Record<CalloutsEditorId, CalloutsEditorData>;
}

export function updateCalloutsData(
    data: Record<CalloutsEditorId, CalloutsEditorData>
): UpdateCalloutsDataAction {
    return {
        type: UPDATE_CALLOUTS_DATA,
        data
    };
}

export type BannerCalloutsActions =
    | OpenCalloutsEditor
    | CloseCalloutsEditor
    | UpdateCalloutsListAction
    | UpdateCalloutsDataAction
    | UpdateAddInputCalloutsEditor
    | ToggleCalloutsEditorItem
    | CalloutsEditorCheckedAllItems
    | CalloutsEditorRemoveItemFromCheckList
    | SaveCallouts
    | AddCallouts
    | RemoveCallouts
    | DeleteCalloutsSuccess
    | AddCalloutsSuccess
    | ShowAddCalloutsError
    | CalloutsEditorRemoveAllItemsFromCheckList
    | HideCalloutsError;
